import {useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';
import {ResourceAllocationState, Team} from '../../../types';
import {removeTeamCascade} from '../cascadeDelete';

type AuthenticatedFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

interface SignalRConnection {
    getConnectionId(): string | null;
}

interface ActionDeps {
    // Ref, not the state value: callbacks read state via stateRef.current so
    // their identities survive state changes — the provider value can then be
    // memoized and downstream memo boundaries hold.
    stateRef: {current: ResourceAllocationState};
    setState: React.Dispatch<React.SetStateAction<ResourceAllocationState>>;
    authenticatedFetch: AuthenticatedFetch;
    signalRService: SignalRConnection;
    customerId: string | undefined;
    baseUrl: string;
}

export const useTeamActions = ({
    stateRef,
    setState,
    authenticatedFetch,
    signalRService,
    customerId,
    baseUrl
}: ActionDeps) => {
    const addTeam = useCallback(
        async (team: Omit<Team, 'id'>): Promise<string> => {
            const id = uuidv4();
            const newTeam = {...team, id};

            // Optimistic update
            setState(prev => ({
                ...prev,
                teams: {...prev.teams, [id]: newTeam}
            }));

            // API call — awaited so callers can sequence dependent creates and
            // surface failures (the server persists this same client-generated id).
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.teams;
                        return {...prev, teams: rest};
                    });

                try {
                    const response = await authenticatedFetch(
                        `${baseUrl}/api/resource-allocation/teams`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SignalR-Connection-Id': connectionId || ''
                            },
                            body: JSON.stringify({
                                id: id,
                                name: team.name,
                                description: team.description,
                                members: team.members || []
                            })
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('[addTeam] API call failed:', errorText);
                        revert();
                        throw new Error(`Failed to create team: ${errorText}`);
                    }
                } catch (error: unknown) {
                    console.error('[addTeam] API call error:', error);
                    revert();
                    throw error;
                }
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updateTeam = useCallback(
        (id: string, updates: Partial<Team>) => {
            const state = stateRef.current;
            // Optimistic update. Under scoped state loading the team may not be
            // in the map — spreading undefined would fabricate a ghost entity,
            // so only patch (and revert) what's present.
            const previousTeam = state.teams[id];
            if (previousTeam) {
                setState(prev => ({
                    ...prev,
                    teams: {
                        ...prev.teams,
                        [id]: {...prev.teams[id], ...updates}
                    }
                }));
            }

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/teams/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updateTeam] API call failed:',
                                await response.text()
                            );
                            if (previousTeam) {
                                setState(prev => ({
                                    ...prev,
                                    teams: {...prev.teams, [id]: previousTeam}
                                }));
                            }
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[updateTeam] API call error:', error);
                        if (previousTeam) {
                            setState(prev => ({
                                ...prev,
                                teams: {...prev.teams, [id]: previousTeam}
                            }));
                        }
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Expire a team (the default "delete" action): disbanded but kept for history
    // so past allocations still resolve. Patched in place, not removed.
    const expireTeam = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousTeam = state.teams[id];
            if (!previousTeam) return;
            const expiredOn = new Date().toISOString();

            setState(prev => ({
                ...prev,
                teams: {
                    ...prev.teams,
                    [id]: {...prev.teams[id], expiredOn}
                }
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    teams: {...prev.teams, [id]: previousTeam}
                }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/teams/${id}`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[expireTeam] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[expireTeam] API call error:', error);
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Un-expire a team — clears expiredOn so it returns to active selection.
    const reinstateTeam = useCallback(
        (id: string) => {
            const state = stateRef.current;
            const previousTeam = state.teams[id];
            if (!previousTeam) return;

            setState(prev => ({
                ...prev,
                teams: {
                    ...prev.teams,
                    [id]: {...prev.teams[id], expiredOn: null}
                }
            }));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    teams: {...prev.teams, [id]: previousTeam}
                }));

            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/teams/${id}/reinstate`,
                    {
                        method: 'POST',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[reinstateTeam] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[reinstateTeam] API call error:', error);
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Permanently delete a team (created-in-error escape hatch). Rejects if the
    // server refuses (assignments reference it) so the caller can surface why.
    const permanentlyDeleteTeam = useCallback(
        async (id: string): Promise<void> => {
            const state = stateRef.current;
            const backup = {
                team: state.teams[id],
                people: Object.fromEntries(
                    Object.entries(state.people).filter(([, p]) =>
                        p.teamIds?.includes(id)
                    )
                )
            };

            // Optimistic update — also scrub the team id from each person's teamIds.
            setState(prev => removeTeamCascade(prev, id));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    teams: {...prev.teams, [id]: backup.team},
                    people: {...prev.people, ...backup.people}
                }));

            if (!customerId || !authenticatedFetch) return;

            const connectionId = signalRService.getConnectionId();
            let response: Response;
            try {
                response = await authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/teams/${id}/permanent`,
                    {
                        method: 'DELETE',
                        headers: {'X-SignalR-Connection-Id': connectionId || ''}
                    }
                );
            } catch (error: unknown) {
                console.error('[permanentlyDeleteTeam] API call error:', error);
                revert();
                throw error;
            }

            if (!response.ok) {
                const message = await response.text();
                console.error(
                    '[permanentlyDeleteTeam] API call failed:',
                    message
                );
                revert();
                throw new Error(message || 'Failed to permanently delete team');
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Merge server-fetched teams into state WITHOUT overwriting anything already
    // loaded — see ensurePeople in usePersonActions.
    const ensureTeams = useCallback(
        (teams: Team[]) => {
            setState(prev => {
                const additions = teams.filter(t => t && !prev.teams[t.id]);
                if (additions.length === 0) return prev;
                const merged = {...prev.teams};
                additions.forEach(t => {
                    merged[t.id] = t;
                });
                return {...prev, teams: merged};
            });
        },
        [setState]
    );

    return useMemo(
        () => ({
            addTeam,
            updateTeam,
            ensureTeams,
            expireTeam,
            reinstateTeam,
            permanentlyDeleteTeam
        }),
        [
            addTeam,
            updateTeam,
            ensureTeams,
            expireTeam,
            reinstateTeam,
            permanentlyDeleteTeam
        ]
    );
};
